export interface ApiClientOptions {
  baseUrl: string
  getToken?: (options?: ApiTokenOptions) => string | null | Promise<string | null>
  /** Extra headers to include on every request (e.g. x-user-id, x-location-id). */
  getExtraHeaders?: () => Record<string, string>
  /** Called on every 401 response, before the rejection is thrown. */
  onUnauthorized?: () => void
}

export interface ApiTokenOptions {
  forceRefresh?: boolean
}

interface SendOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
}

export class ApiClient {
  readonly baseUrl: string
  readonly getToken?: (options?: ApiTokenOptions) => string | null | Promise<string | null>
  readonly getExtraHeaders?: () => Record<string, string>
  readonly onUnauthorized?: () => void

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl
    this.getToken = opts.getToken
    this.getExtraHeaders = opts.getExtraHeaders
    this.onUnauthorized = opts.onUnauthorized
  }

  async buildHeaders(
    extra: Record<string, string> = {},
    tokenOptions?: ApiTokenOptions,
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getExtraHeaders?.(),
      ...extra,
    }
    const token = await this.getToken?.(tokenOptions)
    if (token) headers['Authorization'] = `Bearer ${token}`
    return headers
  }

  async fetchWithAuth(
    path: string,
    init: Omit<RequestInit, 'headers'> = {},
    extraHeaders: Record<string, string> = {},
  ): Promise<Response> {
    const requestInit: Omit<RequestInit, 'headers'> = {
      cache: 'no-store',
      ...init,
    }
    let res = await fetch(`${this.baseUrl}${path}`, {
      ...requestInit,
      headers: await this.buildHeaders(extraHeaders),
    })
    if (res.status === 401 && this.getToken) {
      res = await fetch(`${this.baseUrl}${path}`, {
        ...requestInit,
        headers: await this.buildHeaders(extraHeaders, { forceRefresh: true }),
      })
    }
    if (res.status === 401) {
      this.onUnauthorized?.()
    }
    return res
  }

  /** Single private send-and-check so 401 handling is consistent across verbs. */
  private async send<T>(path: string, opts: SendOptions): Promise<T> {
    const init: RequestInit = {
      method: opts.method,
    }
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body)
    const res = await this.fetchWithAuth(path, init)
    if (res.status === 401) {
      throw new Error(`${opts.method} ${path} failed: 401`)
    }
    if (!res.ok) throw new Error(`${opts.method} ${path} failed: ${res.status}`)
    return res.json() as Promise<T>
  }

  get<T>(path: string): Promise<T> {
    return this.send<T>(path, { method: 'GET' })
  }
  post<T>(path: string, body: unknown): Promise<T> {
    return this.send<T>(path, { method: 'POST', body })
  }
  put<T>(path: string, body: unknown): Promise<T> {
    return this.send<T>(path, { method: 'PUT', body })
  }
  patch<T>(path: string, body: unknown): Promise<T> {
    return this.send<T>(path, { method: 'PATCH', body })
  }
  delete<T>(path: string): Promise<T> {
    return this.send<T>(path, { method: 'DELETE' })
  }
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  return new ApiClient(opts)
}

export * from './slack'
export * from './signals'
export * from './aw-docs'
export * from './aw-metrics'
export * from './teams'
export * from './loop'
export * from './clickup'
export * from './routines'
export * from './routine-runs'
export * from './triggers'
export * from './agent-keys'
export * from './design'
export * from './missions'
